import { motion } from "framer-motion";
import { ArrowRight, Quote, Star, TrendingUp, Shield, Building2 } from "lucide-react";
import { clientReviews } from "@/lib/schemas";

const caseStudies = [
  {
    name: "Parv Bhargava",
    tag: "New Construction",
    location: "Berlin",
    image: "https://cdn.prod.website-files.com/66d0c5e6c019033a6ad45ea9/66e1cece0919c6cf44de989c_Kunde-Parv-Bhargava.avif",
    problem: "Parv was unfamiliar with German regulations and lacked the time to manage a detailed investment that saves taxes while building inflation-protected wealth.",
    solution: "IRE guided him step by step through the process with full transparency. In a comprehensive strategy meeting, he was advised on various approaches and introduced to suitable opportunities.",
    result: "Parv purchased a new build in a prime Berlin location. He now benefits from positive post-tax cash flow, €24,000 annual tax advantage, and significant six-figure wealth accumulation over 10 years.",
    highlight: "€24,000",
    highlightLabel: "Annual Tax Advantage",
    quote: "IRE guided me step by step through every part of the process with full transparency.",
  },
  {
    name: "Dr. Thomas M.A.",
    tag: "Renovation Property",
    location: "Nuremberg",
    image: "https://cdn.prod.website-files.com/66d0c5e6c019033a6ad45ea9/66e1cebf16c634c0487ee9ce_Dr-Thomas-M-A.avif",
    problem: "Dr. Thomas had little time to search for a suitable property that would offer substantial tax benefits with long-term value appreciation potential.",
    solution: "IRE presented tailored solutions and guided him through the entire process. His requirements were assessed and a strategy was developed that perfectly met his needs.",
    result: "Dr. Thomas purchased a renovation property in Nuremberg with special tax deductions for modernization. The multi-family house was modernized with an integrated rental concept, providing significant tax advantages.",
    highlight: "€18,000",
    highlightLabel: "Year-1 Deductions",
    quote: "They presented tailored solutions and guided me through the entire process personally.",
  },
  {
    name: "Ishan Jain",
    tag: "Fully Furnished",
    location: "Berlin",
    image: "https://cdn.prod.website-files.com/66d0c5e6c019033a6ad45ea9/66e1d0563136dfda95b2b64b_Kunde-Ishan-Jain.avif",
    problem: "Ishan was dissatisfied with low returns and taxable profits of traditional investments. He needed a way to optimize taxes while achieving high returns.",
    solution: "IRE presented a comprehensive turnkey real estate investment concept offering tax-optimized returns. A tailored strategy was developed in a detailed consultation.",
    result: "Ishan purchased a move-in ready, fully furnished property in a prime Berlin location. This investment generates annual tax benefits and allows him to build equity tax-free over 10 years.",
    highlight: "Tax-Free",
    highlightLabel: "10-Year Equity",
    quote: "An investment that is tax-efficient and promises high long-term profits.",
  },
];

const ResultsPage = () => {
  return (
    <div className="min-h-screen bg-ire-black pt-32 pb-24" data-testid="results-page">
      {/* Hero */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 mb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-px w-16 bg-ire-accent" />
            <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
              Client Results
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight text-white leading-[1.1] mb-6">
            Real people.
            <br />
            <span className="text-white/40">Real returns.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-white/50 max-w-2xl leading-relaxed">
            Every case study below represents a real household that partnered with IRE Berlin.
            Real names, real numbers, real outcomes.
          </p>
        </motion.div>
      </div>

      {/* Case Studies */}
      {caseStudies.map((study, index) => (
        <div
          key={index}
          className={`py-16 ${index % 2 === 0 ? "bg-ire-black" : "bg-ire-surface"}`}
        >
          <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`result-case-${index + 1}`}
            >
              {/* Top: tag + name + highlight */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-10">
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 border border-ire-accent/25 font-body text-[10px] text-ire-accent tracking-[0.15em] uppercase">
                    {study.tag}
                  </span>
                  <span className="px-3 py-1 border border-white/10 font-body text-[10px] text-white/35 tracking-[0.15em] uppercase">
                    {study.location}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-heading text-3xl lg:text-4xl text-ire-accent tabular-nums">{study.highlight}</p>
                    <p className="font-body text-[10px] text-white/30 tracking-wider uppercase">{study.highlightLabel}</p>
                  </div>
                </div>
              </div>

              {/* Content grid */}
              <div className="grid lg:grid-cols-12 gap-10 items-start">
                {/* Image */}
                <div className={`lg:col-span-4 ${index % 2 !== 0 ? "lg:order-2" : ""}`}>
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={study.image}
                      alt={study.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 border border-white/[0.04]" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ire-black/40 to-transparent" />
                  </div>
                </div>

                {/* Text */}
                <div className={`lg:col-span-8 ${index % 2 !== 0 ? "lg:order-1" : ""}`}>
                  <h2 className="font-heading text-2xl lg:text-3xl font-extralight text-white mb-8">
                    {study.name}
                  </h2>

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="border-l-2 border-white/10 pl-5">
                      <h3 className="font-body text-[10px] tracking-[0.2em] text-white/30 uppercase mb-3">Challenge</h3>
                      <p className="font-body text-sm text-white/45 leading-relaxed">{study.problem}</p>
                    </div>
                    <div className="border-l-2 border-ire-accent/30 pl-5">
                      <h3 className="font-body text-[10px] tracking-[0.2em] text-ire-accent/70 uppercase mb-3">Solution</h3>
                      <p className="font-body text-sm text-white/45 leading-relaxed">{study.solution}</p>
                    </div>
                    <div className="border-l-2 border-ire-accent pl-5">
                      <h3 className="font-body text-[10px] tracking-[0.2em] text-ire-accent uppercase mb-3">Result</h3>
                      <p className="font-body text-sm text-white/60 leading-relaxed">{study.result}</p>
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="bg-ire-surface/50 border border-white/[0.04] p-6 flex gap-4">
                    <Quote className="w-6 h-6 text-ire-accent/30 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-body text-base text-white/50 italic leading-relaxed">
                        "{study.quote}"
                      </p>
                      <div className="flex items-center gap-1 mt-3">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 text-ire-accent fill-ire-accent" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      ))}

      {/* Berlin Market Section */}
      <div className="py-24 bg-ire-surface">
        <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-px w-16 bg-ire-accent" />
              <span className="font-body text-[11px] tracking-[0.4em] text-ire-accent uppercase">
                Market Fundamentals
              </span>
              <div className="h-px w-16 bg-ire-accent" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extralight text-white leading-[1.1] mb-4">
              Germany's housing deficit
              <br /><span className="text-white/40">exceeds 800,000 homes.</span>
            </h2>
            <p className="font-body text-sm text-white/40 max-w-2xl mx-auto leading-relaxed">
              With 680,000-710,000 new immigrants in 2023 alone and construction targets consistently missed,
              demand far outstrips supply. Your investment is backed by demographics, not speculation.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: Building2, value: "800K+", label: "Housing Deficit", accent: true },
              { icon: Shield, value: "0.3%", label: "Berlin Vacancy Rate", accent: false },
              { icon: TrendingUp, value: "710K", label: "Annual Immigrants", accent: false },
              { icon: TrendingUp, value: "3%+", label: "Annual Appreciation", accent: true },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-ire-black border border-white/[0.06] p-6 text-center"
                >
                  <Icon className="w-5 h-5 text-ire-accent/40 mx-auto mb-3" />
                  <p className={`font-heading text-3xl ${stat.accent ? "text-ire-accent" : "text-white"}`}>{stat.value}</p>
                  <p className="font-body text-[10px] text-white/30 mt-2 tracking-wider uppercase">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Testimonials — visible reviews matching the Review/AggregateRating JSON-LD */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-8 md:px-16 lg:px-24 pt-16 sm:pt-24" data-testid="reviews-section" itemScope itemType="https://schema.org/Organization">
        <meta itemProp="name" content="IRE Berlin" />
        <div className="flex items-baseline justify-between flex-wrap gap-3 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px w-12 bg-ire-accent" />
              <span className="font-body text-[10px] tracking-[0.35em] text-ire-accent uppercase">In their own words</span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-white leading-[1.15]">What clients say after closing.</h2>
          </div>
          <div className="flex items-center gap-3 bg-white/[0.02] border border-ire-accent/15 px-4 py-3" itemProp="aggregateRating" itemScope itemType="https://schema.org/AggregateRating">
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} className="w-4 h-4 fill-ire-accent text-ire-accent" />
              ))}
            </div>
            <div className="font-body text-xs text-white/70 leading-tight">
              <div><span itemProp="ratingValue">5.0</span> · <span itemProp="reviewCount">{clientReviews.length}</span> verified clients</div>
              <meta itemProp="bestRating" content="5" />
              <meta itemProp="worstRating" content="1" />
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.06]">
          {clientReviews.map((r, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#050505] p-6 sm:p-7 relative group"
              itemProp="review"
              itemScope
              itemType="https://schema.org/Review"
              data-testid={`review-card-${i}`}
            >
              <meta itemProp="itemReviewed" content="IRE Berlin" />
              <div className="flex items-center gap-0.5 mb-4" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                <meta itemProp="ratingValue" content={String(r.rating)} />
                <meta itemProp="bestRating" content="5" />
                {[1,2,3,4,5].map((i2) => (
                  <Star key={i2} className={`w-3.5 h-3.5 ${i2 <= r.rating ? "fill-ire-accent text-ire-accent" : "text-white/15"}`} />
                ))}
              </div>
              <Quote className="absolute top-5 right-5 w-7 h-7 text-ire-accent/15 group-hover:text-ire-accent/25 transition-colors duration-500" />
              <h3 className="font-heading text-base sm:text-lg text-white leading-snug mb-3" itemProp="name">{r.headline}</h3>
              <p className="font-body text-[13px] text-white/55 leading-relaxed mb-5" itemProp="reviewBody">{r.body}</p>
              <div className="pt-4 border-t border-white/[0.05]">
                <div className="font-body text-xs text-ire-accent" itemProp="author" itemScope itemType="https://schema.org/Person">
                  <span itemProp="name">{r.author}</span>
                </div>
                <div className="font-body text-[11px] text-white/35 mt-1">{r.context}</div>
                <meta itemProp="datePublished" content={r.datePublished} />
              </div>
            </motion.article>
          ))}
        </div>
        <div className="mt-4 font-body text-[10px] text-white/30 tracking-wide">
          Reviews are anonymised at client request. Originals available on file for verification.
        </div>
      </section>

      {/* CTA */}
      <div className="max-w-[1400px] mx-auto px-8 md:px-16 lg:px-24 pt-16">
        <a
          href="#consultation"
          className="group relative flex items-center justify-between p-10 border border-ire-accent/20 bg-gradient-to-r from-ire-accent/[0.04] via-transparent to-ire-accent/[0.04] overflow-hidden hover:border-ire-accent/40 transition-all duration-700"
        >
          <div className="absolute -left-20 -top-20 w-60 h-60 bg-ire-accent/10 rounded-full blur-[80px] group-hover:bg-ire-accent/20 transition-all duration-700" />
          <div className="absolute -right-16 -bottom-16 w-48 h-48 bg-ire-accent/8 rounded-full blur-[60px] group-hover:bg-ire-accent/15 transition-all duration-700" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ire-accent/60 to-transparent" />
          </div>
          <div className="relative z-10">
            <p className="font-body text-[10px] tracking-[0.3em] text-ire-accent/70 uppercase mb-2">Join 4,000+ households</p>
            <p className="font-heading text-2xl lg:text-3xl text-white group-hover:text-ire-accent transition-colors duration-500">Book Your Strategy Call</p>
          </div>
          <div className="relative z-10 w-14 h-14 border border-ire-accent/30 flex items-center justify-center group-hover:bg-ire-accent group-hover:border-ire-accent transition-all duration-500 shrink-0">
            <ArrowRight className="w-5 h-5 text-ire-accent group-hover:text-black group-hover:translate-x-1 transition-all duration-300" />
          </div>
        </a>
      </div>
    </div>
  );
};

export default ResultsPage;
